import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { User, Building2, Phone, Mail, MapPin, Hash } from 'lucide-react';

export const CustomerInfoSection = ({ formData, setFormData, errors, t }) => {
  const updateField = (field, value) => {
    setFormData(prev => ({
      ...prev,
      customerInfo: {
        ...prev.customerInfo,
        [field]: value
      }
    }));
  };

  const c = t.customerInfo;

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="border-0 shadow-lg bg-gradient-card">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-xl text-foreground">
            <User className="w-5 h-5 text-primary" />
            {c.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Salutation */}
          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">{c.salutation}</Label>
            <RadioGroup 
              value={formData.customerInfo.salutation}
              onValueChange={(value) => updateField('salutation', value)}
              className="flex gap-6"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mrs" id="mrs" className="border-primary text-primary" />
                <Label htmlFor="mrs" className="cursor-pointer">{c.mrs}</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mr" id="mr" className="border-primary text-primary" />
                <Label htmlFor="mr" className="cursor-pointer">{c.mr}</Label>
              </div>
            </RadioGroup>
          </div>

          {/* Customer Number and Name Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formData.customerInfo.customerNumber && (
              <div className="space-y-2">
                <Label htmlFor="customerNumber" className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-muted-foreground" />
                  {c.customerNumber}
                </Label>
                <Input
                  id="customerNumber"
                  value={formData.customerInfo.customerNumber}
                  readOnly
                  disabled
                  className="input-focus-ring bg-muted font-mono font-semibold"
                />
              </div>
            )}
            <div className={`space-y-2 ${!formData.customerInfo.customerNumber ? 'md:col-span-2' : ''}`}>
              <Label htmlFor="name" className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                {c.name} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="name"
                value={formData.customerInfo.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder={c.namePlaceholder}
                className={`input-focus-ring ${errors.name ? 'border-destructive' : ''}`}
              />
              {errors.name && (
                <p className="text-xs text-destructive">{errors.name}</p>
              )}
            </div>
          </div>

          {/* Company Info Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                {c.companyName}
              </Label>
              <Input
                id="companyName"
                value={formData.customerInfo.companyName}
                onChange={(e) => updateField('companyName', e.target.value)}
                placeholder={c.companyNamePlaceholder}
                className="input-focus-ring"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="commercialRegister">{c.commercialRegister}</Label>
              <Input
                id="commercialRegister"
                value={formData.customerInfo.commercialRegister}
                onChange={(e) => updateField('commercialRegister', e.target.value)}
                placeholder={c.commercialRegisterPlaceholder}
                className="input-focus-ring"
              />
            </div>
          </div>

          {/* VAT ID */}
          <div className="space-y-2">
            <Label htmlFor="vatId">{c.vatId}</Label>
            <Input
              id="vatId"
              value={formData.customerInfo.vatId}
              onChange={(e) => updateField('vatId', e.target.value)}
              placeholder={c.vatIdPlaceholder}
              className="input-focus-ring"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              {c.address} <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="address"
              value={formData.customerInfo.address}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder={c.addressPlaceholder}
              rows={2}
              className={`input-focus-ring resize-none ${errors.address ? 'border-destructive' : ''}`}
            />
            {errors.address && (
              <p className="text-xs text-destructive">{errors.address}</p>
            )}
          </div>

          {/* Contact Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-muted-foreground" />
                {c.phone} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="phone"
                type="tel"
                value={formData.customerInfo.phone}
                onChange={(e) => updateField('phone', e.target.value)}
                placeholder={c.phonePlaceholder}
                className={`input-focus-ring ${errors.phone ? 'border-destructive' : ''}`}
              />
              {errors.phone && (
                <p className="text-xs text-destructive">{errors.phone}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-muted-foreground" />
                {c.email} <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.customerInfo.email}
                onChange={(e) => updateField('email', e.target.value)}
                placeholder={c.emailPlaceholder}
                className={`input-focus-ring ${errors.email ? 'border-destructive' : ''}`}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email}</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerInfoSection;
